import { EnvironmentId, TagId } from 'api/apiTypes';
import Notification, {
  NotificationSchema,
  NotificationWithId,
  NotificationWithIdSchema,
  NotificationWithoutEnvGuidWithIdSchema,
} from 'api/apiTypes/notification';
import {
  ApiNextNotificationGroupPaths,
  ApiNextNotificationGroupPathsSchema,
} from 'api/apiTypes/notification/groupPaths';
import routes from 'api/routes';
import useSWR, { SWRResponse, useSWRConfig } from 'swr';
import { z } from 'zod';

import { useAxios } from 'utils/transport/useAxios';

import NotificationId from './types/id';

/**
 * Hook to fetch notifications for a specific environment
 */
export default function useNotifications(envGuid: EnvironmentId | null): SWRResponse<NotificationWithId[]> {
  const { get } = useAxios();

  return useSWR(envGuid && routes.notifications.get(envGuid), async (url) => {
    const response = await get(url, { withCredentials: true });
    const finalData: NotificationWithId[] = [];
    if (Array.isArray(response.data)) {
      for (const item of response.data) {
        try {
          finalData.push(NotificationWithIdSchema.parse(item));
        } catch {}
      }
    }
    return finalData;
  });
}

/**
 * Hook that provides notification actions (create, update, delete)
 */
export function useNotificationActions(envGuid: EnvironmentId | null) {
  const { post, put, delete: reqDelete } = useAxios();
  const { mutate } = useSWRConfig();

  const create = async (notification: Notification) => {
    if (!envGuid) return null;

    const payload = NotificationSchema.parse(notification);
    const response = await post(routes.notifications.create(), payload, { withCredentials: true });

    // Revalidate notifications data
    mutate(routes.notifications.get(envGuid));

    return NotificationWithoutEnvGuidWithIdSchema.parse(response.data);
  };

  const update = async (notification: NotificationWithId) => {
    if (!envGuid) return null;

    const payload = NotificationSchema.parse(notification);
    const response = await put(routes.notifications.update(notification._id), payload, { withCredentials: true });

    // Revalidate notifications data
    mutate(routes.notifications.get(envGuid));

    return NotificationWithoutEnvGuidWithIdSchema.parse(response.data);
  };

  const remove = async (id: NotificationId) => {
    if (!envGuid) return null;

    const response = await reqDelete(routes.notifications.delete(id), { withCredentials: true });

    // Revalidate notifications data
    mutate(routes.notifications.get(envGuid));

    return NotificationWithoutEnvGuidWithIdSchema.parse(response.data);
  };

  return {
    create,
    update,
    remove,
  };
}

/**
 * Hook to fetch notification group paths
 */
export function useNotificationsGroupPaths(
  envGuid: EnvironmentId | null,
  tagId: TagId
): SWRResponse<ApiNextNotificationGroupPaths[]> {
  const { get } = useAxios();

  return useSWR(
    envGuid && tagId ? [routes.notifications.groupPaths.get(envGuid, tagId), envGuid, tagId] : null,
    async ([url]) => {
      const response = await get(url, { withCredentials: true });

      const rawSchema = z.record(
        z.object({
          friendlyName: z.string(),
          technicalName: z.string(),
        })
      );

      const rawNotifications = rawSchema.parse(response.data);
      const _notifications = Object.entries(rawNotifications).map(([groupPath, data]) => ({
        ...data,
        groupPath,
      }));

      return ApiNextNotificationGroupPathsSchema.array().parse(_notifications);
    }
  );
}

/**
 * Hook to fetch PII rules for a specific environment
 */
export function usePIIRules(envGuid: EnvironmentId | null): SWRResponse<string[]> {
  const { get } = useAxios();

  return useSWR(envGuid && routes.notifications.piiRules.get(envGuid), async (url) => {
    const response = await get(url, { withCredentials: true });
    return z.array(z.string()).parse(response.data);
  });
}
